import React, { useMemo } from 'react'
import { Table, PrettyNumber, PrettyDate } from '@ui'
import { IColumn } from '@ui/Table'
import { IGroup } from './types'
import { Order, IReportSection } from '@api/types'
import { navigateWithBasepath as navigate } from '../../App'
import { ITooltip } from './types'
import { get } from 'lodash'
import css from './reportTable.module.scss'

interface ITableProps {
  sections: IReportSection[]
  tooltips: ITooltip[]
  table: IGroup
  orderBy: string
  descending: boolean
  showLoadMore: boolean
  isPaging: boolean
  config: any
  sortBy: (s: string) => void
  nextPage: () => void
  isSorting: boolean
  footer?: IGroup
}

interface IRow {
  id: string
  onClick?: () => void
  [key: number]: string
}

export default function ReportTable({
  sections,
  table,
  orderBy,
  descending,
  showLoadMore,
  sortBy,
  nextPage,
  isPaging,
  config,
  isSorting,
  tooltips,
  footer,
}: ITableProps) {
  const { table: tableConfig } = config
  const { columnTypes, columns, values } = table
  // let simpleSections: number[]
  const isNumberColumn = (cType: string) => ['int', 'decimal', 'percent'].includes(cType)

  const simpleSections = sections.reduce((result: number[], s, i) => {
    const current = []
    for (let ind = 0; ind < s.span; ind++) current.push(i)
    return [...result, ...current]
  }, [])

  const tableColumns = useMemo(
    () =>
      columns.reduce((result: IColumn[], c, i) => {
        if (columnTypes[i] === 'id') return result
        const isGray = simpleSections[i] % 2 === 1
        const tooltip = get(tooltips, i)
        // Max column count to break space code
        const MAX_COLUMN_COUNT = 5
        const isSpaceCodeColumn =
          (columns.length > MAX_COLUMN_COUNT && c === 'Space Code') || c === 'Seat'
        return [
          ...result,
          {
            label: c,
            sort: c,
            render: (row: number[]) => {
              const cType = columnTypes[i]

              if (isNumberColumn(cType)) {
                return <PrettyNumber value={row[i]} />
              } else if (cType === 'date') {
                return <PrettyDate value={row[i]} />
              }
              return row[i]
            },
            style: {
              backgroundColor: isGray ? '#fafafa' : '#fff',
              wordBreak: isSpaceCodeColumn ? 'break-all' : 'keep-all',
              width: '110px',
              textAlign: isNumberColumn(columnTypes[i]) ? 'right' : 'inherit',
            },
            tooltip: tooltip && tooltip.description ? tooltip.description : undefined,
          },
        ]
      }, []),
    [columns, columnTypes, simpleSections, tooltips]
  )

  const newValues = values.map((row, idx) => {
    return row.reduce(
      (result: IRow, v, i) => {
        return { ...result, [i]: v }
      },
      {
        id: `${idx}`,
        onClick:
          tableConfig && tableConfig.link
            ? () => navigate(`${tableConfig.link.to}/${row[row.length - 1]}`, {})
            : undefined,
      }
    )
  })

  const prependHeader = (
    <tr>
      {sections.map((s, i) => (
        <th
          className="section"
          key={i}
          colSpan={s.span}
          style={{
            background: i % 2 === 1 ? '#fafafa' : '#fff',
            fontSize: 15,
          }}
        >
          {s.name}
        </th>
      ))}
    </tr>
  )

  return (
    <Table
      stickyHeader={!config.noStickyHeader}
      columns={tableColumns}
      data={newValues}
      sort={
        newValues.length > 1
          ? {
              column: orderBy,
              order: descending ? Order.DESC : Order.ASC,
              onChange: sortBy,
              isSorting: isSorting,
            }
          : undefined
      }
      page={{
        hasNextPage: showLoadMore,
        next: nextPage,
        endCursor: '',
        isPaging,
      }}
      prependHeader={prependHeader}
      appendBody={footer && <TableFooter footer={footer} originalColumns={table.columns} />}
    />
  )
}

function TableFooter({ footer, originalColumns }: { footer: IGroup; originalColumns: string[] }) {
  const actuals = originalColumns.map((oc) => {
    const test = footer.columns.includes(oc)
    if (test) {
      return footer.values[0][footer.columns.findIndex((c) => oc === c)]
    } else return ''
  })

  return (
    <tr className={css.totals_row}>
      {actuals.map((v, i) => (
        <td key={i}>{v}</td>
      ))}
    </tr>
  )
}
